// Shopify Forms renders into the shadow root of <shopify-forms-embed>, out of reach of
// theme CSS, so stylesheets are adopted into that root: a base set plus a variant for the
// section hosting the form. !important is required to beat the app's own inline styles.
(() => {
  const BASE = `
    :host {
      display: block !important;
      width: 99% !important;
      max-width: 99% !important;
      box-sizing: border-box !important;
      --inline-container-max-width: 99% !important;
      --dmi-field-border-color: #20202033;
      --form-field-border: 0 0 0 1px var(--dmi-field-border-color) !important;
      --form-field-border-radius: 5px !important;
      --form-field-padding: 18px !important;
    }

    [class*="_formContainer_"] {
      min-width: unset !important;
      width: 99% !important;
      max-width: 99% !important;
    }
    form {
      width: 99% !important;
      max-width: 99% !important;
      box-sizing: border-box !important;
    }
    input:not([type="submit"]),
    textarea,
    select,
    button {
      font-size: 16px !important;
    }

    [class*="ormFileInputField"] {
      border: 1px solid var(--dmi-field-border-color) !important;
    }

    [class*="electToggle_"] {
      max-height: none !important;
      min-height: 56px !important;
      box-sizing: border-box !important;
    }

    [class*="ultiFieldLine"] {
      align-items: flex-start !important;
    }
  `;

  const VARIANTS = {
    '.retour-aanmelden-form': `
      [class*="ormHeader"],
      [class*="ormTitle"],
      form > h1,
      form > h2 {
        display: none !important;
      }
      [type="submit"] {
        appearance: none !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 10px !important;
        background: #fb5a00 !important;
        color: #fff !important;
        font: 600 20px/1 "Zilla Slab", serif !important;
        height: 50px !important;
        width: auto !important;
        max-width: none !important;
        padding: 0 30px !important;
        margin-top: 8px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        box-sizing: border-box !important;
        cursor: pointer !important;

        &:hover {
          background: #e15100 !important;
        }

        & * {
          line-height: 1 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
      }
      [class*="ormSubmitButtonContainer"] {
        display: flex !important;
        align-items: flex-start !important;
        justify-content: flex-start !important;
        width: auto !important;
        text-align: left !important;
      }
    `,
    '.zakelijk-contact-form': `
      [type="submit"] {
        font-family: "Zilla Slab", serif !important;
        font-size: 20px !important;
        font-weight: 500 !important;
      }
    `,
  };

  // Parsed once, then shared by every shadow root that adopts it.
  const sheets = new Map();
  const sheetFor = (css) => {
    if (!sheets.has(css)) {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(css);
      sheets.set(css, sheet);
    }
    return sheets.get(css);
  };

  const formName = (root, el) =>
    (
      root.querySelector('[class*="extHeading"], [class*="ormTitle"]')?.textContent ||
      el.closest('section, .shopify-section')?.querySelector('h1, h2')?.textContent ||
      ''
    ).trim() || null;

  const enhance = (el) => {
    el.dataset.dmiForms = '1';

    const root = el.shadowRoot;
    const variant = Object.keys(VARIANTS).find((selector) => el.closest(selector));

    root.adoptedStyleSheets = [
      ...root.adoptedStyleSheets,
      sheetFor(BASE),
      ...(variant ? [sheetFor(VARIANTS[variant])] : []),
    ];

    // Delegated, because the app renders the <form> into the shadow root later.
    root.addEventListener(
      'submit',
      () => {
        (window.dataLayer ||= []).push({
          event: 'shopify_form_submit',
          form_id: el.closest('[data-form-root]')?.dataset.formsId ?? null,
          form_name: formName(root, el),
        });
      },
      true
    );
  };

  // The element is upgraded and its shadow root attached a tick or two after it lands.
  const track = (el, frames = 60) => {
    if (el.dataset.dmiForms) return;
    if (el.shadowRoot) enhance(el);
    else if (frames) requestAnimationFrame(() => track(el, frames - 1));
  };

  const scan = (node) => node.querySelectorAll('shopify-forms-embed').forEach((el) => track(el));

  new MutationObserver((records) => {
    for (const { addedNodes } of records) {
      for (const node of addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.localName === 'shopify-forms-embed') track(node);
        else scan(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  scan(document);
})();
